import { ScaleLinear, ScaleTime } from "d3";

import Typography from "ds/components/Typography";

import { WorkerUsageData } from "../types";
import { hasWorkerUsageContract } from "../utils";
import {
  REFERENCE_BADGE_CHAR_WIDTH,
  REFERENCE_BADGE_HEIGHT,
  REFERENCE_BADGE_PADDING_X,
} from "./constants";
import styles from "./styles.module.css";

type ReferenceLabelDatum = {
  key: string;
  text: string;
  borderColor: string;
  backgroundColor: string;
  x: number;
  y: number;
};

type GetReferenceLineLabelsParams = {
  usage: WorkerUsageData;
  lastPercentilePoint?: { day: Date; value: number };
  percentile: number;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
};

export const getReferenceLineLabels = ({
  usage,
  lastPercentilePoint,
  percentile,
  xScale,
  yScale,
}: GetReferenceLineLabelsParams): ReferenceLabelDatum[] => {
  const labels: ReferenceLabelDatum[] = [];

  if (hasWorkerUsageContract(usage)) {
    labels.push({
      key: "contract",
      text: "Contract",
      borderColor: "var(--semantic-color-border-decoration-fuchsia)",
      backgroundColor: "var(--semantic-color-background-decoration-fuchsia-subdued)",
      x: xScale(usage.cycleEnd),
      y: yScale(usage.contractedWorkers),
    });
  }

  if (!lastPercentilePoint) return labels;

  let badgeY = yScale(lastPercentilePoint.value);

  // Near the contract the two badges overlap, so nudge them apart while keeping
  // them ordered like the values they label.
  if (
    hasWorkerUsageContract(usage) &&
    Math.abs(badgeY - yScale(usage.contractedWorkers)) < REFERENCE_BADGE_HEIGHT
  ) {
    badgeY +=
      lastPercentilePoint.value < usage.contractedWorkers
        ? REFERENCE_BADGE_HEIGHT
        : -REFERENCE_BADGE_HEIGHT;
  }

  labels.push({
    key: "percentile",
    text: `P${percentile} to date`,
    borderColor: "var(--semantic-color-border-decoration-orange)",
    backgroundColor: "var(--semantic-color-background-decoration-orange-subdued)",
    x: xScale(lastPercentilePoint.day),
    y: badgeY,
  });

  return labels;
};

type ConcurrencyChartReferenceLineLabelsProps = {
  labels: ReferenceLabelDatum[];
  xMax: number;
};

// Badges are pinned to their line's endpoint and drawn rightward from it, unless
// that would overhang the plot — then they flip and draw leftward instead.
const ConcurrencyChartReferenceLineLabels = ({
  labels,
  xMax,
}: ConcurrencyChartReferenceLineLabelsProps) => (
  <>
    {labels.map((label) => {
      const width = label.text.length * REFERENCE_BADGE_CHAR_WIDTH + REFERENCE_BADGE_PADDING_X * 2;

      const overhangsRight = label.x + width > xMax;
      const x = overhangsRight ? label.x - width : label.x;

      return (
        <foreignObject
          key={label.key}
          x={x}
          y={label.y - REFERENCE_BADGE_HEIGHT / 2}
          width={width}
          height={REFERENCE_BADGE_HEIGHT}
          overflow="visible"
          aria-hidden
        >
          <span
            className={styles.referenceBadge}
            style={{ backgroundColor: label.backgroundColor, borderColor: label.borderColor }}
          >
            <Typography tag="span" variant="p-body4" color="primary">
              {label.text}
            </Typography>
          </span>
        </foreignObject>
      );
    })}
  </>
);

ConcurrencyChartReferenceLineLabels.displayName =
  "UsageWorkersNew.ConcurrencyChart.ReferenceLineLabels";

export default ConcurrencyChartReferenceLineLabels;
